import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import to from "await-to-js";
import cn from "clsx";
import { DialogContent, DialogTitle } from "@mui/material";
import { observer } from "mobx-react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useTranslation } from "react-i18next";
import Stepper from "@/app/csr-src/pages/order/components/stepper";
import styles from "./index.module.scss";
import UKToast from "@/common-components-src/components/UKToast/index";
import UKDialog from "@/common-components-src/components/UKDialog/index";
import UKLoadingIcon from "@/common-components-src/components/UKLoadingIcon";
import UKTruncatedTooltip from "@/common-components-src/components/UKTruncatedTooltip";
import { fetchTraceInfo } from "@/app/csr-src/apis/afterSales/afterSales.api";
import { ITraceInfoReq, RecordTraceVo, UmsMessageVO, SkuListVo } from "@/app/csr-src/apis/afterSales/afterSales.interface";
// 图标
import VectorDown from "@/app/csr-src/assets/icons/VectorDown.svg";
// 组件
import ErrorPage from "@/app/csr-src/pages/order/components/errorPage";
// 埋点
import { sendClickTrack, EXPOSURE_RECORD } from "@/tracks/25362";
import { eventTrackingUtils } from "@/utils/eventTrackingUtils";

interface afsTrackModalProps {
  isOpen: boolean;
  closeHandler: () => void;
}

const afsTrackModal: React.FC<afsTrackModalProps> = observer(({ isOpen, closeHandler }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderId = queryParams.get("orderId") || ""; // 获取订单ID
  const serviceId = queryParams.get("afsServiceId") || ""; // 获取服务单ID

  const [logisticsList, setLogisticsList] = useState<RecordTraceVo | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [curIndex, setCurIndex] = useState<number>(0);
  const [showFoldBtn, setShowFoldBtn] = useState<boolean>(false);
  const [openStates, setOpenStates] = useState<boolean>(false);

  const packageWrapRef = useRef<HTMLDivElement>(null);

  const commonTrackParams = {
    orderId,
    orderStatus: "5" as "1" | "3" | "8" | "6" | "5",
  };

  const loadLogisticslList = async () => {
    const requestData: ITraceInfoReq = { orderId: String(orderId), serviceId };
    const [err, logisticsListData] = await to(fetchTraceInfo(requestData));

    if (err) {
      setError("Failed to fetch order details");
      setLogisticsList(null);
    } else {
      setLogisticsList(logisticsListData as RecordTraceVo);
      // 弹框曝光埋点
      eventTrackingUtils.submitExp({
        event_id: EXPOSURE_RECORD.OrderFollow_LayerExpo,
        json_param: { ...commonTrackParams, packageNum: logisticsListData?.multiPackageList?.length || 0, duration: 300 },
      });
      checkPackageListOverflow();
      setError(null);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadLogisticslList();
  }, []);
  const checkPackageListOverflow = () => {
    setTimeout(() => {
      const containerElement = packageWrapRef.current;
      if (containerElement) {
        const packageWrapHeight = containerElement.clientHeight;
        console.log(packageWrapHeight, "----");
        if (packageWrapHeight > 44) {
          setShowFoldBtn(true);
        } else {
          setShowFoldBtn(false);
        }
      }
    }, 30);
  };

  const { waybillCode, expressName, estDeliveryTime, stationName = "", deliveryAddress = "", umsList = [], multiPackageList = [] } = logisticsList || {};
  // const { promiseTitle, promiseValue, carrierCodeTitle, carrierCode, waybillNumTitle, waybillNum, packageLogistics } = multiPackageList[curIndex] || {};
  const packageLogistics = umsList.map((item) => {
    return {
      message: item.detailDesc,
      messageDate: item.creationTime,
      messageTime: "",
      stateDesc: item.title,
      position: item.position,
    };
  });

  // 埋点需要数据
  const packageNum = `${multiPackageList?.length || 0}`;

  // 复制物流单号
  const copyHandler = () => {
    // 埋点
    sendClickTrack.OrderFollow_TrackIdCopy({
      ...commonTrackParams,
      packageNum,
    });
    UKToast.show({
      content: t("order.orderDetail.common.copySuccessTips"), // 弹窗内容
      icon: "success", // 可选：success, fail, loading, warn, info，影响图标
      duration: 2000, // 不写的话默认3000
    });
  };

  // 计算package items
  const calcNum = (list: SkuListVo[]) => {
    return list.reduce((sum: number, item) => sum + Number(item.skuCount), 0);
  };

  return (
    <UKDialog
      open={isOpen}
      onClose={() => {
        // 埋点
        sendClickTrack.OrderFollow_Close({
          ...commonTrackParams,
          packageNum,
        });
        closeHandler();
      }}
      showCloseBtn={true}
      className={styles.repayOrderModal}
      sx={{
        "& .MuiDialog-paper": {
          width: "680px", // 设置自定义宽度
          height: "76vh",
        },
      }}
    >
      <DialogTitle>Track</DialogTitle>
      <DialogContent>
        {loading ? (
          <div className={styles.loading_wrap}>
            <UKLoadingIcon type="black" />
            <span className={styles.loading_text}>{t("common.pageStatus.loading")}</span>
          </div>
        ) : error ? ( // 异常兜底处理
          <div className={styles.errorWrap}>
            <ErrorPage />
          </div>
        ) : (
          <div className={styles.contentWrap}>
            {/* 一单多包裹场景 */}
            {multiPackageList?.length > 1 ? (
              <div
                className={cn(styles.packageOuterWrap, {
                  [styles.oneLineStyle]: showFoldBtn && !openStates,
                })}
              >
                <div className={styles.packageWrap} ref={packageWrapRef}>
                  {/* 1 Item */}
                  {multiPackageList.map((item, index) => {
                    return (
                      <div
                        key={index}
                        className={cn(styles.packageItem, {
                          [styles.activePackageItem]: index === curIndex,
                        })}
                        onClick={() => {
                          sendClickTrack.OrderTrailFollow_Package({
                            ...commonTrackParams,
                            packageNum,
                            index,
                          });
                          setCurIndex(index);
                        }}
                        exptag={"exp|" + EXPOSURE_RECORD.OrderTrailFollow_PackageExpo}
                        data-exptag-config={JSON.stringify({
                          useClick: false,
                          stay_duration: 0.3,
                          repeated: false,
                          area_rate: 0.5,
                        })}
                        data-exptag-json-param={JSON.stringify({
                          ...commonTrackParams,
                          packageNum,
                          index,
                        })}
                      >
                        Package {index + 1}
                        {calcNum(item?.skuList) ? (
                          <span>({`${calcNum(item?.skuList)} Item${calcNum(item?.skuList) > 1 ? "s" : ""}`})</span>
                        ) : (
                          <span>(Quantity unknown)</span>
                        )}
                      </div>
                    );
                  })}
                </div>
                {showFoldBtn && (
                  <div
                    className={styles.viewAllWrap}
                    onClick={() => {
                      setOpenStates((pre) => !pre);
                    }}
                  >
                    {openStates ? t("order.orderList.orderItems.retractText") : t("checkout.orderCard.viewAll")}
                    <VectorDown
                      className={cn(styles.more_ico, {
                        [styles.retract_ico]: openStates,
                      })}
                    />
                  </div>
                )}
              </div>
            ) : null}
            <ul className={styles.express_content}>
              <li className={styles.express_item}>
                <span>Express</span>
                <span className={cn({ [styles.errorText]: !expressName })}>{expressName || "No data found"}</span>
              </li>
              <li className={styles.express_item}>
                <span>Tracking number</span>
                {waybillCode ? (
                  <div>
                    <span>{waybillCode}</span>
                    <CopyToClipboard text={waybillCode || ""} onCopy={() => copyHandler()}>
                      <span className={styles.copy_btn}>{t("order.orderDetail.common.copyText")}</span>
                    </CopyToClipboard>
                  </div>
                ) : (
                  <div className={styles.errorText}>No data found</div>
                )}
              </li>

              {/* {estDeliveryTime ? (
                <li className={styles.express_item}>
                  <span>Estimated delivery</span>
                  <span>{estDeliveryTime}</span>
                </li>
              ) : null} */}
              <li className={cn(styles.express_item, styles.express_item_special)}>
                <span className={styles.addressTitleStyle}>Drop-off location</span>
                <div className={styles.address_item}>
                  <div className={styles.address_item_inner}>
                    <UKTruncatedTooltip
                      text={stationName}
                      tooltipStyles={styles.address_nick_name}
                      tooltipProps={{
                        placement: "top",
                        arrow: true,
                      }}
                    />
                    {/* <UKTruncatedTooltip
                      text={mobile}
                      tooltipStyles={styles.address_postcode}
                      tooltipProps={{
                        placement: "top",
                        arrow: true,
                      }}
                    /> */}
                  </div>
                  <UKTruncatedTooltip
                    text={deliveryAddress}
                    tooltipStyles={styles.address_detail}
                    tooltipProps={{
                      placement: "top",
                      arrow: true,
                    }}
                  />
                </div>
              </li>
            </ul>

            {/* {logisticsInfoDTOList?.length ? ( */}
            <div className={styles.logistics_content}>
              <Stepper logisticsInfoDTOList={packageLogistics || []} />
            </div>
            {/* ) : null} */}
          </div>
        )}
      </DialogContent>
    </UKDialog>
  );
});

export default afsTrackModal;
